<template>
  <template v-if="isButton">
    <a-button
      type="primary"
      :class="$attrs.class"
      @click="handleGetCopyrightConfirmation"
    >
      {{ description }}
    </a-button>
  </template>
  <CreateCard
    v-else
    :description="description"
    :motion="motion"
    @click="handleGetCopyrightConfirmation"
  >
  </CreateCard>
  <Teleport to="body">
    <a-upload
      class="hidden"
      name="file"
      multiple
      :accept="getAccepts"
      :show-upload-list="false"
      :before-upload="beforeUpload"
      :custom-request="handleUpload"
    />
  </Teleport>
</template>
<script lang="ts" name="FileUpload" setup>
import type {
  FileType,
  UploadChangeParam,
  UploadFile,
} from 'ant-design-vue/es/upload/interface'
import type { UploadRequestOption } from 'ant-design-vue/es/vc-upload/interface'
import {
  AUDIO_ACCEPT,
  FONT_ACCEPT,
  PPT_ACCEPT,
  PDF_ACCEPT,
  IMAGE_ACCEPT,
  VIDEO_ACCEPT,
} from './constant'
import ConFirm from './confirm.vue'
import type { IMediaCard } from '~/components/media'
import { checkFileType, getFormateFileSize } from '@/utils'
import { getCopyrightConfirmation, updateCopyrightConfirmation } from '~/api'
import { confirm } from '@/features/modal'
import { useUploadDrag } from '~/hooks/useModal/modals/upload'
import { useActionIntercept } from '~/hooks/useActionIntercept'
import { FILE_SIZE, getSetingKey } from '~/hooks/useUpload'
import DataSource from '@/features/oss-upload/data-source'
import XOssService, {
  useRegisterRefreshRemove,
} from '@/features/oss-upload/service'
import type { UserInfo } from '~/api/interface'

const { $t } = useNuxtApp()

export interface IFileUpload {
  description?: string
  motion?: boolean
  isPersonal?: boolean
  type: number
  modelValue?: IMediaCard[]
  uploadParams?: any // 上传请求参数
  isButton?: boolean
  pageType: number
}

const props = withDefaults(defineProps<IFileUpload>(), {
  description: '上传文件',
  motion: false,
  isButton: false,
})

const emit = defineEmits([
  'update:modelValue',
  'update-data',
  'save',
  'changeLoading',
  'updateTotal',
])

defineOptions({
  inheritAttrs: false,
})

const uploadList = computed({
  get: () => props.modelValue,
  set: (v) => {
    emit('update:modelValue', v)
    emit('update-data', v)
  },
})

/**
 * 支持上传的文件类型
 */
const getAccepts = computed(() => {
  let accept: string[] = []
  if (props.type === RESOURCE_TYPE.FONT) {
    accept = [FONT_ACCEPT.join(',.')]
  } else if (props.type === RESOURCE_TYPE.MATERIAL) {
    const videoList = VIDEO_ACCEPT.map((item) => `.${item}`)
    const imageList = IMAGE_ACCEPT.map((item) => `.${item}`)
    const list = [...PPT_ACCEPT, ...PDF_ACCEPT].map((item) => `.${item}`)
    accept = [...videoList, ...imageList, ...list]
  } else if (
    [RESOURCE_TYPE.MUSIC, RESOURCE_TYPE.SOUND_EFFECT].includes(props.type)
  ) {
    accept = AUDIO_ACCEPT.map((item) => `.${item}`)
  } else if (props.type === RESOURCE_TYPE.PASTER) {
    accept = IMAGE_ACCEPT.map((item) => `.${item}`)
  } else if (
    props.type === RESOURCE_TYPE.HEAD ||
    props.type === RESOURCE_TYPE.END
  ) {
    accept = VIDEO_ACCEPT.map((item) => `.${item}`)
  }

  return accept.join(',')
})

const getUploadDom = () => {
  const input = document.querySelector('input[type=file]') as HTMLInputElement
  input?.click?.()
}

// 片头为5，片尾为13
// const handleClickUpload = (value: number) => {
//   getUploadDom();
// };

type FILEType = File & { uid: string }

const userInfo = useUserInfo()

const uploadSecretIds: string[] = []

useRegisterRefreshRemove(uploadSecretIds)

const startUpload = async (file: FILEType) => {
  trackEvent(TRACK_EVENT.YOUYAN_PLATFORM_UPLOAD, {
    target:
      props.type === RESOURCE_TYPE.MATERIAL
        ? TARGET_FROM.Material
        : props.type === RESOURCE_TYPE.FONT
          ? TARGET_FROM.Font
          : props.type === RESOURCE_TYPE.MUSIC
            ? TARGET_FROM.BGM
            : props.type === RESOURCE_TYPE.SOUND_EFFECT
              ? TARGET_FROM.Audio
              : TARGET_FROM.Sticker,
    source: props?.pageType === 0 ? SOURCE.MYSPACE : SOURCE.TEAM_SPACE,
    fileSize: file.size,
    fileType: file.type,
  })
  const index: any = uploadList.value?.findIndex(
    (item: any) => item.id === file?.uid
  )

  const onProgress = (percent: number, extras: XOSS.IExtras) => {
    if (
      xossService.upload_secret_id &&
      !uploadSecretIds.includes(xossService.upload_secret_id)
    ) {
      uploadSecretIds.push(xossService.upload_secret_id)
    }

    const index: any = uploadList.value?.findIndex(
      (item: any) => item.id === extras?.uid
    )
    if (index !== -1) {
      const record: any = uploadList?.value?.[index]
      if (percent) {
        record.status = EXECUTION_STATUS.PROCESSING
        record.message = $t('shang-chuan-zhong')
        record.percent = percent
      }
    }
  }

  const onFail = (msg: string, extras: XOSS.IExtras) => {
    if (index !== -1 && xossService?.upload_secret_id) {
      const record: any = uploadList?.value?.[index]
      uploadList?.value?.splice(index, 1, {
        ...record,
        upload_secret_id: xossService.upload_secret_id,
      })
    }

    emit('save', xossService?.upload_secret_id)
    if (
      extras?.info?.hint &&
      [
        HINT_MAP.CONTACT_THE_ADMINISTRATOR,
        HINT_MAP.UPGRADE_PERSON,
        HINT_MAP.BUY_STORAGE,
      ].includes(extras?.info?.hint) &&
      extras?.info?.reason === 5
    ) {
      useActionIntercept({ type: extras.info.hint })
    }
  }

  const onSuccess = (media: XOSS.ISuccess) => {
    emit('save', {
      ...media?.relatedObject,
      upload_secret_id: xossService?.upload_secret_id,
    })
    useUpdateUser()
  }
  const suffix = file.name?.split('.')?.at(-1)?.toLowerCase() || ''
  const xossService = new XOssService({
    auth: {
      user_id: userInfo.value ? userInfo.value.user_id : '',
    },
    dataSource: DataSource,
    params: {
      resource_type: props.type as XOSS.TResourceType,
      upload_type: props?.pageType as XOSS.TUploadType,
    },
    is_transcoding: VIDEO_ACCEPT.includes(suffix),
    ignore_query_status: true,
    onProgress,
    onSuccess,
    onFail,
  })
  await xossService.upload(file)
  if (index !== -1 && xossService?.upload_secret_id) {
    const record: any = uploadList?.value?.[index]
    uploadList?.value?.splice(index, 1, {
      ...record,
      upload_secret_id: xossService.upload_secret_id,
    })
  }
}

/**
 * 自定义上传
 * @param info
 */
const handleUpload = ({ file }: UploadRequestOption) => {
  handleChange(file as FILEType)

  Modal.destroyAll()

  const index =
    uploadList.value?.findIndex(
      (item: any) => item.id === (file as FileType).uid
    ) ?? -1
  if (index !== -1) {
    startUpload(file as FILEType)
  }
}

const checkFile = (type: string) => getAccepts?.value?.includes(type)
const remove = (id: string) => {
  uploadList.value = uploadList.value?.filter((item: any) => item.id !== id)
}

const getTypeName = () => {
  if (props.type === RESOURCE_TYPE.FONT) {
    return $t('zi-ti')
  } else if (props.type === RESOURCE_TYPE.MUSIC) {
    return $t('yin-xiao')
  } else if (props.type === RESOURCE_TYPE.SOUND_EFFECT) {
    return $t('yin-xiao')
  } else if (props.type === RESOURCE_TYPE.MATERIAL) {
    return $t('su-cai')
  }

  return ''
}

const agree = ref(false)

const handleUpdateCopyrightConfirmation = async () => {
  try {
    await updateCopyrightConfirmation({
      upload_type: props?.pageType,
      resource_type: props?.type,
    })
  } catch (error) {
    console.error(error)
  }
}

const handleUploadConfirm = () => {
  const onCancel = async () => {
    const res = await modal
    res.destroy()
  }
  const onOk = async () => {
    const res = await modal
    res.destroy()
    _open()
    handleUpdateCopyrightConfirmation()
  }

  const name = getTypeName()
  const modal = confirm({
    icon: null,
    footer: null,
    width: 520,
    centered: true,
    content: h(ConFirm, {
      onCancel,
      onOk,
      title: $t('name-ban-quan-que-ren', [name]),
      description: $t(
        'wo-yi-he-shi-bing-que-ren-ji-jiang-shang-chuan-de-name-yi-huo-de-quan-li-ren-de-shang-ye-shi-yong-shou-quan-fou-ze-you-ci-chan-sheng-de-fa-lv-ze-ren-you-wo-zi-hang-cheng-dan',
        [name]
      ),
    }),
  })
}

const handleGetCopyrightConfirmation = async () => {
  if (props.type === RESOURCE_TYPE.PASTER) {
    _open()
    return
  }
  try {
    const response: any = await getCopyrightConfirmation({
      upload_type: props?.pageType,
      resource_type: props.type,
    })
    if (response?.code === 0) {
      agree.value = response?.data?.had_read
      if (
        [
          RESOURCE_TYPE.FONT,
          RESOURCE_TYPE.MUSIC,
          RESOURCE_TYPE.SOUND_EFFECT,
          RESOURCE_TYPE.MATERIAL,
        ].includes(props.type) &&
        !response?.data?.had_read
      ) {
        handleUploadConfirm()
      } else {
        _open()
      }
    }
  } catch (error) {
    console.error(error)
  }
}

const getStringLength = (str: string, max: number): string => {
  let len = 0
  let result = ''

  const list = str.split('')
  try {
    list.forEach((item) => {
      const chineseRegex = /[\u4E00-\u9FA5]/g
      const chinese = item?.match(chineseRegex)
      if (chinese) {
        len += 2
      } else {
        len += 1
      }

      if (len <= max) {
        result = result + item
      } else {
        throw new Error('stop')
      }
    })
  } catch (e: any) {
    console.error(e)

    if (e?.massage === 'stop') {
      throw e
    }
  }

  return result
}

const handleChange = (file: FILEType) => {
  if (!file) {
    return false
  }

  const { uid, size, name } = file

  let async = 1
  let media_type: FILE_TYPE_MAP = FILE_TYPE_MAP.IMAGE // 素材分类 0为图片

  const suffix = file.name?.split('.')?.at(-1)?.toLowerCase() || ''
  if (VIDEO_ACCEPT.includes(suffix)) {
    media_type = FILE_TYPE_MAP.VIDEO
  }

  if (PDF_ACCEPT.includes(suffix)) {
    async = 1
    media_type = FILE_TYPE_MAP.PDF
  }
  if (PPT_ACCEPT.includes(suffix)) {
    async = 1
    media_type = FILE_TYPE_MAP.PPT
  }
  if (AUDIO_ACCEPT.includes(suffix)) {
    media_type = FILE_TYPE_MAP.AUDIO
  }

  const account: any = {}
  if (props.pageType === UPLOAD_TYPE.TEAM) {
    account.name = userInfo.value?.account_name
    account.avatar = transformImage(userInfo.value?.logo_url)
  }

  const current: any = {
    id: uid,
    title: getStringLength(name.slice(0, name.indexOf(suffix)), 24),
    oldName: name,
    status: 10,
    media_size: size, // 原文件大小
    size: getFormateFileSize(size), // 格式化文件大小
    type: [RESOURCE_TYPE.MUSIC, RESOURCE_TYPE.SOUND_EFFECT].includes(props.type)
      ? CARD_TYPE.MUSIC
      : CARD_TYPE.MATERIAL,
    file,
    retry: () => handleUpload({ file } as unknown as UploadRequestOption), // 重新上传
    remove: () => remove(uid), // 删除
    async,
    allow: true,
    ...account,
  }

  if (props.type === RESOURCE_TYPE.MATERIAL) {
    current.media_type = media_type
  }

  const sizes = FILE_SIZE
  const key = getSetingKey(suffix) as keyof typeof FILE_SIZE
  if (key) {
    const limit = sizes[key] as keyof UserInfo['rights_record']
    const settingSize = userInfo.value?.rights_record![limit] as number
    if (file.size! > settingSize) {
      // message.error(
      //   `${suffix.toUpperCase()}文件大小不能超过${transSize(
      //     settingSize
      //   )}MB，当前${transSize(file.size)}MB`
      // )
      // return false
      current.allow = false
    }
  }

  uploadList.value?.unshift(current)

  emit('updateTotal')
}

/**
 * 上传校验
 * @param file
 */
const beforeUpload = async (file: FileType) => {
  const suffix = file.name?.split('.')?.at(-1)?.toLowerCase() || ''
  let isMatchFile = false

  // const userRights = getCookie(COOKIE_KEY.USER_RIGHTS) as any
  if (checkFile(`.${suffix?.toLocaleLowerCase()}`)) {
    isMatchFile = true
  }
  if (!isMatchFile) {
    message.destroy()
    message.warning($t('wen-jian-ge-shi-bu-he-fa'))
    return false
  }
  if (file.type.includes('image')) {
    const isImgMatch = await checkFileType(file)
    if (!isImgMatch) {
      message.error(
        h('span', null, [
          h('a', { href: 'javascript:;', class: 'text-info-1' }, file.name),
          $t('wen-jian-hou-zhui-yu-shi-ji-ge-shi-bu-fu-shang-chuan-shi-bai'),
        ])
      )
      return false
    }
  }

  return true
}

const _open = () => {
  useUploadDrag({
    resourceType: props?.type,
    title: props?.description,
    onOpen: () => getUploadDom(),
    onFileChange: async (files) => {
      for (const file of files) {
        const flag = await beforeUpload(file as FileType)
        if (flag) {
          handleUpload({ file } as UploadRequestOption)
        }
      }
    },
  })
}

defineExpose({ sizeList: [] })
</script>
<style lang="scss" scoped>
.horizontal {
  @apply flex justify-center items-center w-[72px] h-[47px] bg-back-4 border-[1px] border-solid border-back-5;
  &:before {
    @apply w-full h-[30px] bg-back-1 border-solid border-[1px] border-back-5 my-[8px];
    content: '';
  }
}
.vertical {
  @apply flex justify-center items-center w-[72px] h-[47px] bg-back-4 border-[1px] border-solid border-back-5;
  &:before {
    @apply w-[32px] h-full bg-back-1 border-solid border-[1px] border-back-5 my-[8px];
    content: '';
  }
}

.material {
  @apply w-[calc((100%-8px*1)/2)] text-start;
}

.popover {
  width: 200px;
}

:deep(.content) {
  &:hover {
    background: var(--purple-2);
    border-color: var(--purple-5);
  }
}
</style>
